import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { LoginCredentials } from '../types/auth';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card } from '../components/ui/Card';
import { ForgotPasswordModal } from '../components/modals/ForgotPasswordModal';

/**
 * Página de login con diseño moderno y elegante
 */
export const Login: React.FC = () => {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<Partial<LoginCredentials>>({});
  const [loginError, setLoginError] = useState<string>('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);

  /**
   * Valida los campos del formulario
   */
  const validateForm = (): boolean => {
    const newErrors: Partial<LoginCredentials> = {};

    if (!credentials.email) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email)) {
      newErrors.email = 'El formato del email no es válido';
    }

    if (!credentials.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (credentials.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el cambio en los campos del formulario
   */
  const handleInputChange = (field: keyof LoginCredentials, value: string) => {
    setCredentials(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    if (loginError) {
      setLoginError('');
    }
  };

  /**
   * Maneja el envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await login(credentials.email, credentials.password);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error en login:', error);
      setLoginError(
        error instanceof Error 
          ? error.message 
          : 'Error al iniciar sesión. Verifica tus credenciales.'
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-900 to-secondary-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Header con logo */}
        <div className="text-center space-y-6">
          <div className="mx-auto w-32 h-32 bg-gradient-to-r from-primary-500 to-primary-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-primary-500/20">
            <img 
              src="/cornella.png" 
              alt="Cornellà" 
              className="w-20 h-20 object-contain"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-secondary-50">
              CorneApp
            </h1>
            <p className="text-secondary-400 text-base">
              Inicia sesión en tu cuenta
            </p>
          </div>
        </div>

        {/* Formulario */}
        <Card className="p-8 space-y-6 bg-secondary-800/90 backdrop-blur-sm border border-secondary-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error general */}
            {loginError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <p className="text-red-400 text-sm text-center">{loginError}</p>
              </div>
            )}

            {/* Campo Email */}
            <Input
              label="Correo electrónico"
              type="email"
              value={credentials.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              icon={<Mail className="h-5 w-5" />}
              placeholder="tu@email.com"
              error={errors.email}
              disabled={isLoading}
            />

            {/* Campo Contraseña */}
            <Input
              label="Contraseña"
              type="password"
              value={credentials.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              icon={<Lock className="h-5 w-5" />}
              placeholder="••••••••"
              error={errors.password}
              disabled={isLoading}
              showPasswordToggle={true}
            />

            {/* Botón de envío */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Iniciando sesión...</span>
                </div>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>
            
            {/* Enlace para resetear contraseña */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => setShowForgotPasswordModal(true)}
                className="text-primary-400 hover:text-primary-300 text-sm font-medium transition-colors duration-200"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        </Card>

        {/* Footer */}
        <div className="text-center">
          <p className="text-secondary-400 text-sm">
            © 2025 CorneApp. Todos los derechos reservados.
          </p>
        </div>
      </div>
      
      {/* Modal de reseteo de contraseña */}
      <ForgotPasswordModal
        isOpen={showForgotPasswordModal}
        onClose={() => setShowForgotPasswordModal(false)}
      />
    </div>
  );
};

export default Login;